/* HOQ product-page price policy. The existing quote dialog handles the form. */
(() => {
  'use strict';
  if (window.HOQPricePolicy) { window.HOQPricePolicy.mount(); return; }
  const controllers = new Map();
  const validPrice = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;

  class PricePolicy {
    constructor(node) {
      this.node = node;
      this.config = JSON.parse(node.textContent);
      this.root = document.getElementById('shopify-section-' + this.config.sectionId);
      this.style = document.getElementById('HOQPricePolicyStyle-' + this.config.sectionId);
      if (!this.root || !this.style) throw new Error('Missing product price policy scope');
      this.prices = new Map(Object.entries(this.config.prices || {}));
      if (validPrice(this.config.initialPrice)) this.prices.set(this.config.initialVariantId, this.config.initialPrice);
      this.listeners = new AbortController();
      this.watched = new WeakMap();
      this.variantId = undefined;
      this.active = false;
      this.pending = false;
      this.sequence = 0;
      const signal = this.listeners.signal;
      this.root.addEventListener('change', () => this.schedule(), {signal});
      ['theme:variant:change', 'variant:change', 'variant:changed'].forEach((name) => {
        document.addEventListener(name, (event) => {
          const variant = event.detail?.variant;
          const id = String(variant?.id || '');
          if (!this.root.contains(event.target) && !this.prices.has(id)) return;
          if (id && validPrice(variant.price)) this.prices.set(id, variant.price);
          this.schedule();
        }, {signal});
      });
      this.root.addEventListener('submit', (event) => this.guard(event), {capture: true, signal});
      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-add-to-cart], button[name="add"], .shopify-payment-button')) this.guard(event);
      }, {capture: true, signal});
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.root, {childList: true, subtree: true, attributes: true, attributeFilter: ['value']});
      // A small scoped fallback also catches themes that silently replace input setters.
      this.timer = setInterval(() => {
        if (!this.root.isConnected) this.destroy();
        else if (!document.hidden) this.sync();
      }, 400);
      window.addEventListener('pageshow', () => this.sync(), {signal});
      this.sync();
    }

    input() {
      const trigger = this.root.querySelector('[data-hoq-open][data-hoq-product-form]');
      const form = trigger && document.getElementById(trigger.dataset.hoqProductForm);
      return form?.querySelector('input[name="id"]') || this.root.querySelector('product-form input.product-variant-id');
    }

    watch(input) {
      if (!input) return;
      const own = Object.getOwnPropertyDescriptor(input, 'value');
      if (own?.set && this.watched.get(input) === own.set) return;
      const previous = own || Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      if (!previous?.get || !previous?.set || previous.configurable === false) return;
      const policy = this;
      const setter = function(value) { previous.set.call(this, value); policy.schedule(); };
      // Preserve the existing inventory/availability setter on this exact input.
      Object.defineProperty(input, 'value', {get: previous.get, set: setter, configurable: true, enumerable: previous.enumerable});
      this.watched.set(input, setter);
    }

    schedule() {
      if (this.scheduled) return;
      this.scheduled = true;
      queueMicrotask(() => { this.scheduled = false; if (!this.destroyed) this.sync(); });
    }

    sync() {
      const input = this.input();
      this.watch(input);
      const id = String(input ? input.value : this.config.initialVariantId || '');
      if (id !== this.variantId) {
        this.variantId = id;
        this.sequence++;
        this.request?.abort();
        this.request = null;
        this.pending = false;
        this.error = '';
        if (id && !this.prices.has(id) && !this.config.manual) {
          this.pending = true;
          this.loadPrice(id, this.sequence);
        }
      }
      const price = this.prices.get(id);
      this.active = this.config.manual || (validPrice(price) && price > this.config.threshold);
      this.apply();
    }

    apply() {
      const hide = this.active || this.pending;
      const media = hide ? 'all' : 'not all';
      if (this.style.media !== media) this.style.media = media;
      this.root.querySelectorAll('[data-hoq-request-trigger]').forEach((wrap) => {
        wrap.hidden = !this.active;
        const button = wrap.querySelector('[data-hoq-open]');
        if (button) button.dataset.hoqVariant = this.variantId;
      });
      this.root.querySelectorAll('.hide-above-three-lakh').forEach((node) => { node.hidden = hide; });
      this.root.querySelectorAll('[data-hoq-price-status]').forEach((node) => {
        const message = this.error || (this.pending ? 'Updating price…' : '');
        if (node.textContent !== message) node.textContent = message;
        node.hidden = !message;
      });
    }

    async loadPrice(id, sequence) {
      const request = new AbortController();
      this.request = request;
      const timeout = setTimeout(() => request.abort(), 12000);
      try {
        const url = new URL(this.config.url, location.origin);
        url.searchParams.set('section_id', 'hoq-price-request-data');
        url.searchParams.set('variant', id);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url.href, {credentials: 'same-origin', signal: request.signal, headers: {Accept: 'text/html'}});
        if (!response.ok) throw new Error('Price unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const data = JSON.parse(html.querySelector('[data-hoq-product-data]')?.textContent || 'null');
        if (String(data?.id) !== this.config.productId || String(data?.variant?.id) !== id || !validPrice(data?.variant?.price)) throw new Error('Invalid price data');
        if (sequence !== this.sequence || this.destroyed) return;
        this.prices.set(id, data.variant.price);
        this.pending = false;
        this.error = '';
        this.sync();
      } catch (_) {
        if (sequence !== this.sequence || this.destroyed) return;
        // Keep purchase controls hidden until the current option can be checked.
        this.error = 'Could not load this option’s price. Please select another option or refresh the page.';
        this.apply();
      } finally { clearTimeout(timeout); }
    }

    guard(event) {
      const form = event.target.closest('form');
      if (event.type === 'submit' && !form?.matches('[data-product-form], [data-type="add-to-cart-form"]')) return;
      this.sync();
      if (!this.active && !this.pending) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.active) this.root.querySelector('[data-hoq-request-trigger] [data-hoq-open]')?.click();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;
      this.sequence++;
      this.request?.abort();
      this.observer.disconnect();
      this.listeners.abort();
      clearInterval(this.timer);
      controllers.delete(this.config.sectionId);
    }
  }

  function mount() {
    document.querySelectorAll('[data-hoq-price-policy]').forEach((node) => {
      let id;
      try { id = JSON.parse(node.textContent).sectionId; } catch (_) { return; }
      const existing = controllers.get(id);
      if (existing?.node === node) return;
      existing?.destroy();
      try { controllers.set(id, new PricePolicy(node)); } catch (error) { console.warn('[HOQ] Price policy:', error.message); }
    });
  }
  window.HOQPricePolicy = {mount};
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', (event) => controllers.get(event.detail?.sectionId)?.destroy());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, {once: true});
  else mount();
})();
